import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  AcceptInviteResponseSchema,
  CreateInviteResponseSchema,
  InvitePreviewSchema,
  InviteSchema,
  InvitesListResponseSchema,
  type CreateInviteRequest,
  type Invite,
  type InvitePreview,
} from '@repo/api-client/dtos';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteJSON, getJSON, postJSON } from '../internal/http';

/**
 * Public invite preview by token. Used by the apps/web fallback
 * landing page (and the mobile invite-accept screen) to show the
 * recipient *what* they're being invited to before they act.
 *
 * Doesn't require auth — the token is the secret. The UI must
 * still treat the response carefully: rendering a different
 * empty state for `pending` vs `expired` vs `revoked` vs
 * `accepted` so the recipient knows what to do next.
 */
export function useInvitePreview(token: string | undefined) {
  const http = useHttpClient();

  const {
    data,
    error,
    isPending: loading,
  } = useQuery({
    queryKey: token
      ? queryKeys.invites.preview(token)
      : ['invites', 'preview', 'idle'],
    queryFn: () =>
      getJSON(
        http,
        `/invites/preview/${encodeURIComponent(token ?? '')}`,
        InvitePreviewSchema,
      ),
    enabled: !!token,
    // Preview is read-mostly and never useful to refetch on focus —
    // the underlying invite state only changes via the accept flow,
    // which already invalidates this key.
    staleTime: 30_000,
  });

  return { preview: data as InvitePreview | undefined, loading, error };
}

/** Pending invites for an org. */
export function useInvites(overrideOrgId?: string) {
  const http = useHttpClient();
  const activeOrgId = useActiveOrganizationId();
  const orgId = overrideOrgId ?? activeOrgId;

  const {
    error,
    isPending: loading,
    data,
    refetch,
  } = useQuery({
    queryKey: orgId ? queryKeys.invites.list(orgId) : ['invites', 'idle'],
    queryFn: () => getJSON(http, `/orgs/${orgId}/invites`, InvitesListResponseSchema),
    enabled: !!orgId,
  });

  return { invites: data as Invite[] | undefined, loading, error, refetch };
}

/**
 * Create an invite. v0.1 stubs the mailer and returns the bare token
 * alongside the persisted row so screens can show the accept URL for
 * manual sharing.
 */
export function useCreateInvite() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ orgId, ...body }: CreateInviteRequest & { orgId: string }) =>
      postJSON(
        http,
        `/orgs/${orgId}/invites`,
        body,
        CreateInviteResponseSchema,
      ),
    onSuccess: (_data, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invites.list(orgId) });
    },
  });

  return {
    createInvite: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/** Revoke a pending invite. */
export function useRevokeInvite() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ orgId, inviteId }: { orgId: string; inviteId: string }) =>
      deleteJSON(http, `/orgs/${orgId}/invites/${inviteId}`, InviteSchema),
    onSuccess: (_data, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.invites.list(orgId) });
    },
  });

  return {
    revokeInvite: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}

/**
 * Accept an invite by token. The server creates a `Membership` for
 * the current user; we invalidate the organizations list so the
 * shell can pick up the new org.
 */
export function useAcceptInvite() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (token: string) =>
      postJSON(
        http,
        '/invites/accept',
        { token },
        AcceptInviteResponseSchema,
      ),
    onSuccess: (data, token) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.list(data.invite.orgId),
      });
      // The preview now reflects `accepted` instead of `pending` —
      // invalidate so any cached web/mobile preview consumers
      // re-fetch and render the "already a member" empty state.
      queryClient.invalidateQueries({ queryKey: queryKeys.invites.preview(token) });
    },
  });

  return {
    acceptInvite: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
